import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AssetMeasurement } from '../../../../model/asset-measurement';
import { Tenant } from '../../../../model/tenant';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MeasurementType } from '../../../../model/measurement-type';
import { TenantsService } from '../../../../services/tenants.service';
import { ActionButton } from '../../../../model/ui/action-button';
import { AssetsService } from '../../../../services/assets.service';
import { Asset } from '../../../../model/asset';
import { noSpecialCharacters } from '../../../../shared/custom-validators';

@Component({
  selector: 'app-assets-form-measurements',
  templateUrl: './assets-form-measurements.component.html',
  styleUrls: ['./assets-form-measurements.component.scss']
})
export class AssetsFormMeasurementsComponent implements OnInit {

  @Input() mode: 'create' | 'edit';

  @Input() assetMeasurements: AssetMeasurement[];
  @Output() assetMeasurementsChange = new EventEmitter<AssetMeasurement[]>();
  @Output() onPreviousStep = new EventEmitter<void>();
  @Output() onNextStep = new EventEmitter<void>();
  @Output() onCancel = new EventEmitter<void>();

  @Input() tenant: Tenant;
  tenantIsEmpty: boolean;

  assetMeasurementsForm: FormGroup;
  rowsForm: FormArray;

  measurementTypes: MeasurementType[];
  clonedMeasurements: { [s: string]: AbstractControl; } = {};

  displayCopyFromAsset: boolean;
  copyFromAssetActions: ActionButton[];

  copyAssetMeasurements: AssetMeasurement[];

  constructor(private tenantsService: TenantsService,
              private assetsService: AssetsService,
              private formBuilder: FormBuilder) { }

  ngOnInit(): void {
    this.buildCopyFromAssetActions();
    if (!this.tenant) {
      this.tenantIsEmpty = true;
    } else {
      this.loadAndCreateForm();
    }
  }

  private loadAndCreateForm(): void {
    this.tenantsService.getTenantMeasurementTypes(this.tenant.uuid).toPromise().then(response => {
      this.measurementTypes = response.measurementTypes
        .sort((a, b) => a.sensor > b.sensor ? 1 : -1);
      this.createForm(this.assetMeasurements);
      this.copyAssetMeasurements = this.assetMeasurements;
    });
  }

  private createForm(assetMeasurements: AssetMeasurement[]): void {
    this.rowsForm = this.formBuilder.array(
      assetMeasurements.map(measurement => this.formGroupFromAssetMeasurement(measurement))
    );
    this.assetMeasurementsForm = this.formBuilder.group({
      assetMeasurements: this.rowsForm
    });
  }

  addMeasurement(): void {
    const newMeasurement = this.formGroupFromAssetMeasurement(new AssetMeasurement());
    this.rowsForm.push(newMeasurement);
  }

  onRowDelete(rowIndex: number): void {
    this.rowsForm.removeAt(rowIndex);
  }

  deleteAllRows(): void {
    while (this.rowsForm.length > 0) {
      this.rowsForm.removeAt(0);
    }
  }

  previousStep(): void {
    const measurements = this.assetMeasurementsForm.get('assetMeasurements').value;
    this.onPreviousStep.emit();
    this.assetMeasurementsChange.emit(measurements);
  }

  nextStep(): void {
    if (this.assetMeasurementsForm.valid) {
      const measurements = this.assetMeasurementsForm.get('assetMeasurements').value;
      measurements.forEach(sensor => {
        if (sensor.uuid == null) {
          sensor.uuid = this.copyAssetMeasurements.find(copySensor => copySensor.name === sensor.name)?.uuid;
        }
      });
      this.onNextStep.emit();
      this.assetMeasurementsChange.emit(measurements);
    }
  }

  private formGroupFromAssetMeasurement(measurement: AssetMeasurement): FormGroup {
    return this.formBuilder.group({
      name: [ measurement.name, [
        Validators.maxLength(255),
        Validators.required,
        noSpecialCharacters
      ]],
      description: [ measurement.description, [Validators.maxLength(255)] ],
      alias: [ measurement.alias ],
      measurementType: [ measurement.measurementType, [Validators.required] ],
      sampleRate: [ measurement.sampleRate, [Validators.min(1)] ],
      uuid: [ measurement.uuid ]
    });
  }

  cancelAction(): void {
    this.onCancel.emit();
  }

  private buildCopyFromAssetActions(): void {
    this.copyFromAssetActions = [
      {
        label: 'misc.copySensors',
        icon: 'pi pi-copy',
        action: (asset: Asset) => this.copyMeasurements(asset)
      }, {
        label: 'misc.appendSensors',
        icon: 'pi pi-plus',
        action: (asset: Asset) => this.copyMeasurements(asset, true)
      }
    ];
  }

  copyMeasurements(asset: Asset, append: boolean = false): void {
    this.assetsService.getAssetMeasurementsOfAsset(asset.uuid).subscribe(response => {
      if (!append) {
        this.deleteAllRows();
      }
      const measurements = this.assetMeasurementsForm.get('assetMeasurements').value;
      response.assetMeasurements.forEach((measurement: AssetMeasurement) => {
        measurement.uuid = this.copyAssetMeasurements.find(copyMeasurement => copyMeasurement.name === measurement.name)?.uuid;
        if (measurements.find(sensor => sensor.name === measurement.name) === undefined) {
          this.rowsForm.push(this.formGroupFromAssetMeasurement(measurement));
        }
      });
      this.displayCopyFromAsset = false;
    });
  }

}
